<template>
    <div class="row justify-content-center pt-5 mt-4">
        <div class="col-sm-11 col-md-9 col-lg-7 col-xl-6 col-xxl-5">
            <v-form @submit="findTrip" v-slot="{errors, meta, submitCount, isSubmitting}" id="FindTripForm" ref="findTripForm">
                <overlay-container class="find-trip card" :active="isSubmitting" :text="$t('find.findingYourTrip')">
                    <div class="card-body pt-4 pb-5">
                        <h3 class="text-center">{{$t('find.pageTitle')}}</h3>
                        <v-field
                            name="recordLocator"
                            :label="$t('validate.fields.confirmationNumber')"
                            rules="required|alpha_num"
                            v-slot="{field, errorMessage}"
                        >
                            <div class="form-floating mt-4 input-group-lg">
                                <input
                                    type="text"
                                    v-bind="field"
                                    id="RecordLocator"
                                    placeholder="ex. PY4MYT"
                                    class="form-control"
                                    :class="{'is-invalid': !!errorMessage}"
                                />
                                <label for="RecordLocator">
                                    <font-awesome-icon class="text-secondary fa-lg fa-fw me-2" :icon="['fal', 'square-check']" />
                                    <span class="d-none d-sm-inline">{{$t('find.confirmationNumberOrRecordLocator')}}</span>
                                    <span class="d-inline d-sm-none">{{$t('find.confirmationNumber')}}</span><!--Smaller translation for phone screens-->
                                </label>
                            </div>
                            <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                        </v-field>

                        <v-field
                            name="lastName"
                            :label="$t('validate.fields.lastName')"
                            rules="required"
                            v-slot="{field, errorMessage}"
                        >
                            <div class="form-floating mt-4 input-group-lg">
                                <input
                                    type="text"
                                    v-bind="field"
                                    id="LastName"
                                    placeholder="last name"
                                    class="form-control"
                                    :class="{'is-invalid': !!errorMessage}"
                                />
                                <label for="LastName">
                                    <font-awesome-icon class="text-secondary fa-lg fa-fw me-2" :icon="['fal', 'user']" />
                                    {{$t('find.travelersLastName')}}
                                </label>
                            </div>
                            <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                        </v-field>
                        <div class="d-grid mt-4">
                            <invisible-recaptcha
                                @verified="recaptchaVerified"
                                @expired="recaptchaExpired"
                                @failed="recaptchaFailed"
                                ref="invisibleRecaptcha"
                            />

                            <button
                                type="submit"
                                class="btn btn-lg btn-primary w-100"
                                :disabled="isSubmitting"
                            >
                                {{$t('find.pageTitle')}}
                            </button>

                            <div v-if="errors.submit" class="text-danger text-center mt-3">
                                <strong>
                                    <font-awesome-icon class="me-1" :icon="['fal', 'circle-exclamation']" /> {{errors.submit}}
                                </strong>
                            </div>
                            <div v-else-if="!meta.valid && submitCount > 0" class="text-danger text-center mt-3">
                                <strong>
                                    <font-awesome-icon class="me-1" :icon="['fal', 'circle-exclamation']" /> {{$t('find.errors.default')}}
                                </strong>
                            </div>

                            <button
                                type="button"
                                v-if="trip !== null && trip.tripId !== undefined"
                                @click="redirectToTrip"
                                class="btn btn-outline-dark mt-5"
                            >
                                <strong>{{$t('find.backToTrip')}}</strong>
                                <br/>
                                <small>
                                    <span v-if="trip.pnr">{{trip.pnr.recordLocator}} &bull;</span>
                                    {{trip.name}}
                                    <span v-if="trip.cityPairs">&bull; {{trip.cityPairs}}</span>
                                </small>
                            </button>
                        </div>
                    </div>
                </overlay-container>
            </v-form>
        </div>
    </div>
</template>

<script>
//External components
import { Form as VForm, Field as VField} from 'vee-validate';

//P4MT Components
import InvisibleRecaptcha from '@/components/common/InvisibleRecaptcha';
import OverlayContainer from '@/components/common/OverlayContainer';
import { findTrip } from '@/api';

export default {
    name: 'Find',
    components: {
        OverlayContainer,
        InvisibleRecaptcha,
        VForm,
        VField
    },
    data() {
        return {
            recaptchaResponse: null
        }
    },
    computed: {
        trip() {
            return this.$store.state.trip;
        }
    },
    methods: {
        async findTrip(values, actions) {
            //If we don't have a response yet, go get it.
            if (this.recaptchaResponse === null) {
                //TODO This is a dirty way to call a function from our child component InvisibleRecaptcha.
                this.$refs.invisibleRecaptcha.execute();
            } else {
                return findTrip(values.recordLocator, values.lastName, this.recaptchaResponse)
                    .then(response => {
                        this.$store.commit('setTrip', response.data);
                        this.redirectToTrip();
                    })
                    .catch(() => {
                        actions.setErrors({
                            submit: this.$t('find.errors.notFound')
                        });

                        //TODO This is a dirty way to call a function from our child component InvisibleRecaptcha.
                        this.$refs.invisibleRecaptcha.reset();
                    });
            }

        },
        redirectToTrip() {
            this.$router.push({
                name: 'trip',
                params: {
                    tripId: this.trip.tripId,
                    tripTinyHash: this.trip.tinyHash
                }
            });
        },
        recaptchaFailed() {
            //This is not best practice for vee-validate, but would have had to perform nastier gymnastics to get into findTrip function.
            this.$refs.findTripForm.setFieldError('submit', this.$t('find.errors.recaptcha'));
        },
        recaptchaVerified(response) {
            if (response !== null) {
                this.recaptchaResponse = response;

                //TODO this is a hack. We need to resubmit the form after recaptcha is verified.
                document.getElementById('FindTripForm').requestSubmit();
            }
        },
        //after a recaptcha expires (or is reset), clear the recaptcha response.
        recaptchaExpired() {
            this.recaptchaResponse = null;
        }
    }
}
</script>

<style lang="scss" scoped>
    .find-trip {
        .card-body {
            padding-left: 3rem;
            padding-right: 3rem;
        }
    }

    //Reduce padding on xs breakpoint.
    @include media-breakpoint-only(xs) {
        .find-trip {
            .card-body {
                padding-left: 1.25rem;
                padding-right: 1.25rem;
            }
        }
    }
</style>